'use client'

/**
 * Custom hook to handle wallet authentication and user session management
 * Manages:
 * - Wallet connection state
 * - User authentication flow
 * - Login method tracking
 * - User info fetching coordination
 */

// React core imports
import { useEffect, useMemo, useCallback } from 'react'

// External libraries and utilities
import { useLogin, usePrivy } from '@privy-io/react-auth'
import localStorage from '@locmod/local-storage'

// Internal application modules
import { type ConnectorName, useWallet } from 'wallet'
import { useUserContext } from 'src/contexts/user'
import { useUserInfoFetch } from './useUserInfoFetch'
import { useUtmTracking } from './useUtmTracking'
import { constants } from 'helpers'

export const useWalletAuth = () => {
  // Privy authentication state
  const { user, authenticated } = usePrivy()
  const { setLoginMethod, loginMethod } = useUserContext()
  // UTM and invite code tracking
  const { storedInviteCode, storedUtmParams } = useUtmTracking()

  // Wallet connection state
  const {
    account,
    isReconnecting,
    isConnecting,
    isConnected,
    isAAWallet,
    connector,
  } = useWallet()

  // Memoize connector name to prevent unnecessary re-renders
  const connectorName = useMemo(
    () => (connector ? { name: connector.name as ConnectorName } : null),
    [connector]
  )

  // Get wallet address based on wallet type
  const walletAddress = useMemo(
    () => (isAAWallet ? user?.smartWallet?.address : user?.wallet?.address),
    [isAAWallet, user?.smartWallet?.address, user?.wallet?.address]
  )

  // User info fetching hook
  const { getUserInfo } = useUserInfoFetch({
    isAAWallet,
    user,
    loginMethod,
    connector: connectorName,
    storedInviteCode,
    storedUtmParams,
  })

  // Login handler with error handling
  const { login } = useLogin({
    onComplete: useCallback(
      ({ loginMethod }: { loginMethod: string | null }) => {
        try {
          setLoginMethod(loginMethod)
        } catch (error) {
          console.error('Error setting login method:', error)
        }
      },
      [setLoginMethod]
    ),
    onError: useCallback(error => {
      console.error('Login error:', error)
    }, []),
  })

  // Clear fetch status when disconnected
  useEffect(() => {
    if (!isConnected) {
      localStorage.removeItem(constants.localStorageKeys.hasFetchedUserInfo)
    }
  }, [isConnected])

  // Fetch user info when authenticated
  useEffect(() => {
    if (
      localStorage.getItem(constants.localStorageKeys.hasFetchedUserInfo) !==
        'true' &&
      authenticated &&
      walletAddress
    ) {
      localStorage.setItem(
        constants.localStorageKeys.hasFetchedUserInfo,
        'true'
      )
      getUserInfo()
    }
  }, [authenticated, walletAddress])

  return {
    account,
    isReconnecting,
    isConnecting,
    isConnected,
    connectOrCreateWallet: (...args) => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('manualLoginThisSession', 'true')
      }
      return login(...args)
    },
    authenticated,
  }
}
